import React from 'react'
import { Tabs } from 'components/FbUI/Tabs'
import { formatInteger } from 'utils/formatting'
import { UnifiedSearchTable } from './UnifiedSearchTable'
import { UnifiedSearchEmptyState } from './UnifiedSearchEmptyState'
import { ContactCompanySearchResult, DoorSearchResult } from 'models/companies'

// Format count with "+" indicator if at the 10,000 cap
const formatCountWithCap = (count: number): string => {
  if (count === 10000) {
    return '10,000+'
  }
  return formatInteger(count)
}

// Union type for all search results - provides better type safety than any[]
type SearchResultUnion = ContactCompanySearchResult | DoorSearchResult

interface UnifiedSearchResultsProps {
  selectedTab: 'firstbite' | 'unmatched' | 'firstbitedoors' | 'unmatcheddoors'
  setSelectedTab: (
    tab: 'firstbite' | 'unmatched' | 'firstbitedoors' | 'unmatcheddoors'
  ) => void
  firstBiteCount: number
  unmatchedCount: number
  firstBiteDoorCount: number
  unmatchedDoorCount: number
  currentResults: SearchResultUnion[] // Now properly typed instead of any[]
  debouncedSearchTerm: string
  isLoading: boolean
  currentTotalRows: number
  pageSize: number
  currentPage: number
  setCurrentPage: (page: number) => void
  onRowClick: (row: SearchResultUnion) => void
  selectedItem?: SearchResultUnion | null
  showRadioButtons?: boolean
}

export function UnifiedSearchResults(props: UnifiedSearchResultsProps) {
  const {
    selectedTab,
    setSelectedTab,
    firstBiteCount,
    unmatchedCount,
    firstBiteDoorCount,
    unmatchedDoorCount,
    currentResults,
    debouncedSearchTerm,
    isLoading,
    currentTotalRows,
    pageSize,
    currentPage,
    setCurrentPage,
    onRowClick,
    selectedItem,
    showRadioButtons = false,
  } = props
  const handleTabChange = (
    tab: 'firstbite' | 'unmatched' | 'firstbitedoors' | 'unmatcheddoors'
  ) => {
    setSelectedTab(tab)
  }

  const hasResults = currentResults.length > 0
  const shouldShowTable = debouncedSearchTerm && (hasResults || isLoading)
  const shouldShowEmptyState =
    !debouncedSearchTerm || (debouncedSearchTerm && !isLoading && !hasResults)

  return (
    <div className="flex-1">
      {/* Tabs - always show the tabs */}
      <div className="px-3">
        <Tabs
          tabs={[
            {
              key: 'firstbite',
              title: `FB Companies (${formatCountWithCap(firstBiteCount)})`,
            },
            {
              key: 'firstbitedoors',
              title: `FB Locations (${formatCountWithCap(firstBiteDoorCount)})`,
            },
            {
              key: 'unmatched',
              title: `Custom Companies (${formatCountWithCap(unmatchedCount)})`,
            },
            {
              key: 'unmatcheddoors',
              title: `Custom Locations (${formatCountWithCap(unmatchedDoorCount)})`,
            },
          ]}
          selectedTab={selectedTab}
          setSelectedTab={handleTabChange}
          style={{ borderBottom: 'none' }}
        />
      </div>

      {/* Results */}
      <div className={`overflow-hidden flex flex-col h-[510px]`}>
        {/* Show empty state only when not loading and no results */}
        {shouldShowEmptyState && (
          <UnifiedSearchEmptyState
            debouncedSearchTerm={debouncedSearchTerm}
            isLoading={isLoading}
            selectedTab={selectedTab}
            hasResults={hasResults}
          />
        )}

        {/* Show table when we have a search term and either have results or are loading */}
        {shouldShowTable && (
          <UnifiedSearchTable
            data={currentResults}
            isLoading={isLoading}
            selectedTab={selectedTab}
            currentTotalRows={currentTotalRows}
            pageSize={pageSize}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            onRowClick={onRowClick}
            selectedItem={selectedItem}
            showRadioButtons={showRadioButtons}
          />
        )}
      </div>
    </div>
  )
}
